import * as React from 'react'
import { dispatchToProps } from './ContactsList.container'
import { moment } from 'features/Moment'
import { Table, TableHeader, TableRow } from 'features/Table'
import Pagination from 'components/Pagination'
import EmptyMessageCard from 'components/Cards/EmptyMessageCard'
import Button from 'components/Button'
import { Row, Col } from 'components/Layout'
import cn from 'classnames'
import * as sLay from 'styles/modules/layout.scss'
import * as sMarg from 'styles/modules/margins.scss'
import * as sText from 'components/Text/Text.scss'
import { useGetContactsQuery } from 'src/generated/graphql'
import LoadingOverlay from 'components/Loading/LoadingOverlay'
import { joinAndCompact } from '../../../utils/lodash'
import { Field } from 'redux-form'
import { Input } from 'components/Input/Input'
import useUnmount from 'react-use/lib/useUnmount'

export interface IContactsListComponentProps {
  page: number
  search?: string
  replace: typeof dispatchToProps['replace']
  showContactCreateModal: typeof dispatchToProps['showContactCreateModal']
  showContactImportModal: typeof dispatchToProps['showContactImportModal']
}

export const ContactsListComponent = (props: IContactsListComponentProps) => {
  const { page, search, replace, showContactCreateModal, showContactImportModal } = props
  const searchTimeoutRef = React.useRef<any>(undefined)

  const onSearchChange = (search: string) => {
    // if we still entering search term, cancel previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // in order to minify number of API requests
    // and give user enough time to enter a search term,
    // we postpone requesting a little bit
    searchTimeoutRef.current = setTimeout(() => {
      replace({
        pathname: '/dashboard/contacts',
        query: { search, page: 1 },
      })
    }, 300)
  }

  useUnmount(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
  })

  const { data, loading } = useGetContactsQuery({
    variables: {
      page,
      search,
    },
  })

  if (!data || !data.contacts || loading) return <LoadingOverlay transparent />
  const { results, totalPages } = data.contacts
  return (
    <div>
      <div
        className={cn(
          sMarg.mb30,
          sLay['layout-gt-xs-column'],
          sLay['layout-gt-xs-row'],
          sLay['layout-align-start-center']
        )}
      >
        <div className={cn(sLay.flex, sText.descrip3, sMarg.mb15xs)}>
          Add contacts automatically.{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://help.thinkpickle.com.au/contacts/adding-contacts"
          >
            Learn how.
          </a>
        </div>
        <Row className={cn(sLay['layout-gt-xs-column'], sLay['layout-gt-xs-row'], 'sm')}>
          <Col className={cn('sm', sMarg.mb15xs)}>
            <Field
              component={props => <Input {...props} label="Search" hideDot display="box" />}
              name="search"
              onChange={event => onSearchChange(event ? event.target.value : '')}
            />
          </Col>
          <Col className={cn('sm')}>
            <Button
              className={sMarg.mb15xs}
              color="tertiary"
              size="sm"
              ghost
              onClick={() => showContactImportModal({})}
              rect
              style={{ width: '100%' }}
            >
              Import
            </Button>
          </Col>
          <Col className={cn('sm')}>
            <Button
              color="tertiary"
              size="sm"
              onClick={() => showContactCreateModal({})}
              rect
              style={{ width: '100%' }}
            >
              Create Contact
            </Button>
          </Col>
        </Row>
      </div>
      {results.length === 0 ? (
        <EmptyMessageCard
          loading={loading}
          title="You have no contacts yet."
          subtitle="This will appear once your flows are triggered"
        />
      ) : (
        <div>
          <Table
            header={
              <TableHeader>
                <div className={sLay['hide-xs']}>Last active</div>
                <div>Number</div>
                <div className={sLay['hide-xs']}>Name</div>
                <div className={sLay['hide-xs']} style={{ maxWidth: '90px' }}>
                  # Flows
                </div>
                <div style={{ maxWidth: '76px' }} />
              </TableHeader>
            }
          >
            {results.map(contact => {
              const lastActive = moment(contact.lastActive).format('DD MMM YYYY, h:mm:ss a')
              const name = joinAndCompact([contact.company, contact.firstName, contact.lastName])
              return (
                <TableRow key={contact.id}>
                  <div className={sLay['hide-xs']} title={lastActive}>
                    {lastActive}
                  </div>
                  <div title={(contact.phoneNumbers && contact.phoneNumbers.join(', ')) || ''}>
                    {contact.phoneNumbers &&
                      contact.phoneNumbers.map((num, idx) => <div key={idx}>{num}</div>)}
                  </div>
                  <div className={sLay['hide-xs']} title={name}>
                    {name}
                  </div>
                  <div className={sLay['hide-xs']} style={{ maxWidth: '90px' }}>
                    {contact.flowLogs ? contact.flowLogs.totalResults : 0}
                  </div>
                  <div style={{ maxWidth: '76px' }}>
                    <Button
                      color="tertiary"
                      size="xs"
                      ghost
                      to={`/dashboard/contacts/${contact.id}`}
                    >
                      View
                    </Button>
                  </div>
                </TableRow>
              )
            })}
          </Table>
        </div>
      )}
      <Pagination
        page={page}
        numPages={totalPages}
        onClick={nextPage =>
          replace({
            pathname: location.pathname,
            query: {
              page: nextPage,
              search,
            },
          })
        }
      />
    </div>
  )
}



// WEBPACK FOOTER //
// src/features/Contacts/ContactsList/ContactsList.tsx